import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { PermissionAction } from "@/lib/constants";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  Contexto de autenticação
 *
 *  Portado do strategybox (`src/lib/auth-context.tsx`). Cada um dos nove
 *  detalhes abaixo codifica um incidente real de produção. NÃO simplificar
 *  nenhum deles sem reproduzir primeiro o problema que resolveram:
 *
 *   1. `onAuthStateChange` é registado ANTES do bootstrap `getSession()`.
 *      Inverter a ordem perde o evento INITIAL_SESSION.
 *   2. A hidratação de perfil/papéis/permissões é adiada com `setTimeout(…, 0)`.
 *      Chamar `supabase.from()` de forma síncrona dentro do callback do
 *      `onAuthStateChange` BLOQUEIA o supabase-js: o lock de auth ainda está
 *      retido e a query nunca resolve.
 *   3. `withTimeout()` em getSession e signIn. Com localStorage corrompido o
 *      `getSession()` fica pendurado para sempre e o utilizador vê um spinner
 *      eterno em vez do ecrã de login.
 *   4. `clearLocalAuthState()` remove todas as chaves `sb-*` e NUNCA lança.
 *   5. `hydrationSeqRef` descarta respostas fora de ordem. Sem isto, trocar de
 *      utilizador depressa deixa as permissões do anterior em memória.
 *   6. `Promise.allSettled` (não `all`): uma política de RLS a falhar não pode
 *      apagar o perfil inteiro.
 *   7. `TOKEN_REFRESHED` com sessão nula é transitório — nunca destruir estado.
 *   8. `signOut` corre contra um temporizador e limpa localmente de qualquer forma.
 *   9. `hasPermission` faz curto-circuito para admin.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface Profile {
  id: string;
  user_id: string;
  full_name: string | null;
  email: string | null;
  approved: boolean;
  is_external_user: boolean;
  /** Projeto ativo do utilizador. É a fonte primária de `current_projeto_id()`. */
  projeto_ativo_id: string | null;
  pessoa_id: string | null;
  organizacao_id: string | null;
}

/** As capacidades que não cabem em ler/criar/editar/excluir (PL-005, 15/09). */
export type Capacidade = "cronograma_datas" | "cronograma_percentual" | "baseline" | "subprojetos";

export interface ModulePermissions {
  module: string;
  can_read: boolean;
  can_create: boolean;
  can_update: boolean;
  can_delete: boolean;
  scope: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isAdmin: boolean;
  isApproved: boolean;
  modules: string[];
  modulePermissions: ModulePermissions[];
  /** Resolução da sessão inicial. */
  loading: boolean;
  /**
   * Hidratação de perfil/papéis/permissões em curso.
   *
   * SEPARADO de `loading` de propósito. A sessão resolve-se muito antes do
   * perfil chegar, e durante essa janela `profile` é null — logo `isApproved`
   * é false. Sem esta flag, uma guarda de rota conclui "não aprovado" e
   * redireciona um utilizador perfeitamente aprovado para o ecrã de espera,
   * onde ele fica preso porque esse ecrã já não reavalia.
   */
  hydrating: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (data: {
    email: string;
    password: string;
    fullName: string;
  }) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  /** Troca a senha do próprio utilizador, pela sessão ativa (compliance). */
  updatePassword: (novaSenha: string) => Promise<{ error: any }>;
  refreshProfile: () => Promise<void>;
  hasPermission: (module: string, action: PermissionAction) => boolean;
  /** O papel no projeto ativo — `admin` para o administrador global. */
  papel: string | null;
  temCapacidade: (capacidade: Capacidade) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SIGN_IN_TIMEOUT_MS = 8000;
const GET_SESSION_TIMEOUT_MS = 3000;
const SIGN_OUT_TIMEOUT_MS = 2500;

/** Remove sincronamente todas as chaves `sb-*` do localStorage. Nunca lança. */
function clearLocalAuthState() {
  try {
    const keys = Object.keys(localStorage).filter((k) => k.startsWith("sb-"));
    keys.forEach((k) => {
      try {
        localStorage.removeItem(k);
      } catch {
        /* uma chave que não sai não pode impedir a limpeza das outras */
      }
    });
  } catch {
    /* localStorage indisponível (modo privado, política de terceiros) */
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label}_TIMEOUT`)), ms),
    ),
  ]);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [modules, setModules] = useState<string[]>([]);
  const [modulePermissions, setModulePermissions] = useState<ModulePermissions[]>([]);
  const [papel, setPapel] = useState<string | null>(null);
  const [capacidades, setCapacidades] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [hydrating, setHydrating] = useState(false);

  const hydrationSeqRef = useRef(0);

  /**
   * O utilizador cujas permissões já estão em memória.
   *
   * ── Porque existe (28/08, slide 51) ────────────────────────────────────
   * O supabase-js liga um handler de `visibilitychange`: ao voltar ao
   * separador emite `SIGNED_IN` ou `TOKEN_REFRESHED`. Isso punha `hydrating`
   * a `true`, o `ProtectedRoute` trocava o `<Outlet/>` por um esqueleto, e o
   * `AppShell` DESMONTAVA — levando atrás o menu colapsado, o scroll da
   * grelha, os nós expandidos da árvore e o texto da busca.
   *
   * Rehidratar as permissões ao voltar está certo. Bloquear o ecrã enquanto
   * isso acontece é que não: já temos perfil, papéis e módulos em memória, e
   * são os mesmos até prova em contrário.
   *
   * ⚠ Bloquear continua a ser obrigatório quando o UTILIZADOR muda. Sem isso,
   * quem entra com outra conta vê por um instante os módulos da anterior — e
   * é precisamente o que o `hydrationSeqRef` foi criado para impedir.
   */
  const hidratadoParaRef = useRef<string | null>(null);

  const resetReactAuthState = useCallback(() => {
    hidratadoParaRef.current = null;
    setSession(null);
    setUser(null);
    setProfile(null);
    setIsAdmin(false);
    setModules([]);
    setModulePermissions([]);
    setPapel(null);
    setCapacidades({});
    setHydrating(false);
  }, []);

  /** Aplica sessão/utilizador de forma síncrona — nunca bloqueia em rede. */
  const applyBasicSession = useCallback((s: Session | null) => {
    setSession(s);
    setUser(s?.user ?? null);
  }, []);

  /**
   * Hidratação em segundo plano de perfil, papéis e permissões por módulo.
   * Nunca bloqueia o login.
   *
   * Nota sobre `user_module_access`: as permissões no TGT são POR PROJETO. Não
   * filtramos aqui por `projeto_id` porque a política RESTRICTIVE de isolamento
   * já limita as linhas ao projeto ativo do utilizador. A base é a única fonte
   * de verdade do escopo — replicar o filtro no cliente só criaria uma segunda
   * definição para manter em sincronia.
   */
  const hydrateAccessData = useCallback(async (userId: string, bloquear = true) => {
    const seq = ++hydrationSeqRef.current;
    // ⚠ Levantar a bandeira é condicional; baixá-la NUNCA é. Se um ciclo
    // bloqueante for ultrapassado por um de fundo, só o mais recente a baixa —
    // e ele tem de a baixar mesmo que não a tenha levantado, ou fica presa.
    if (bloquear) setHydrating(true);
    try {
      // ── As permissões vêm da matriz do PROJETO (PL-005, 15/09) ─────────────
      // Eram as linhas de `user_module_access`, gravadas uma vez na aprovação:
      // mudar o papel de alguém não lhe mexia nas permissões, e o papel do
      // projeto nunca era lido. `minhas_permissoes()` resolve o papel da pessoa
      // no projeto ativo e devolve a matriz desse papel — a mesma que
      // `has_module_permission()` usa na base, portanto o ecrã e a RLS não
      // podem discordar.
      const [profileRes, rolesRes, permsRes] = await Promise.allSettled([
        supabase.from("profiles").select("*").eq("user_id", userId).maybeSingle(),
        supabase.from("user_roles").select("role").eq("user_id", userId),
        supabase.rpc("minhas_permissoes"),
      ]);

      // Resposta obsoleta: outro ciclo de hidratação começou entretanto.
      if (seq !== hydrationSeqRef.current) return;

      const profileData =
        profileRes.status === "fulfilled"
          ? ((profileRes.value.data as Profile | null) ?? null)
          : null;
      const rolesData = rolesRes.status === "fulfilled" ? rolesRes.value.data : [];
      const perms = (permsRes.status === "fulfilled" ? permsRes.value.data : null) as {
        papel: string | null;
        modulos: { modulo: string; ler: boolean; criar: boolean; editar: boolean; excluir: boolean }[];
        capacidades: Record<string, boolean>;
      } | null;

      const admin = rolesData?.some((r: any) => r.role === "admin") ?? false;
      // ⚠ Falhando a chamada, NADA é permitido — e não "tudo". Uma permissão
      // por omissão verdadeira abria botões que a base recusaria a seguir.
      const userModulePerms: ModulePermissions[] = (perms?.modulos ?? []).map((m) => ({
        module: m.modulo,
        can_read: m.ler,
        can_create: m.criar,
        can_update: m.editar,
        can_delete: m.excluir,
        scope: "all",
      }));

      setProfile(profileData);
      setIsAdmin(admin);
      setModules(userModulePerms.filter((m) => m.can_read).map((m) => m.module));
      setModulePermissions(userModulePerms);
      setPapel(perms?.papel ?? (admin ? "admin" : null));
      setCapacidades(perms?.capacidades ?? {});
      hidratadoParaRef.current = userId;
    } catch (err) {
      console.error("hydrateAccessData falhou:", err);
    } finally {
      // Só o ciclo MAIS RECENTE baixa a bandeira. Um ciclo obsoleto a
      // terminar depois deixaria a aplicação a achar que já hidratou,
      // quando o ciclo válido ainda vem a caminho.
      if (seq === hydrationSeqRef.current) setHydrating(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // 1) Listener PRIMEIRO. Leve, não bloqueante.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;

      if (event === "SIGNED_OUT") {
        resetReactAuthState();
        return;
      }

      // Sessão nula transitória durante o refresh — nunca destruir estado aqui.
      if (event === "TOKEN_REFRESHED" && !newSession) return;

      applyBasicSession(newSession);

      // Adiar a hidratação para fora do callback do SDK: chamar supabase.from()
      // aqui de forma síncrona bloqueia o cliente (o lock de auth está retido).
      if (newSession?.user) {
        const uid = newSession.user.id;
        // Bloquear só quando ainda não há permissões DESTE utilizador em
        // memória — a primeira vez, ou uma conta diferente.
        //
        // Levantar a bandeira SINCRONAMENTE, antes do setTimeout. Caso
        // contrário abre-se uma janela com sessão presente, `hydrating` a
        // falso e perfil ainda nulo — e é nessa janela que uma guarda de rota
        // conclui, erradamente, que o utilizador não está aprovado.
        const primeiraVez = hidratadoParaRef.current !== uid;
        if (primeiraVez) setHydrating(true);
        setTimeout(() => {
          if (mounted) void hydrateAccessData(uid, primeiraVez);
        }, 0);
      }
    });

    // 2) Bootstrap: ler a sessão uma vez, com timeout.
    void (async () => {
      try {
        let initialSession: Session | null = null;
        try {
          const result = await withTimeout(
            supabase.auth.getSession(),
            GET_SESSION_TIMEOUT_MS,
            "GET_SESSION",
          );
          initialSession = result?.data?.session ?? null;
        } catch {
          // Estado local corrompido ou com lock preso: limpar uma vez e
          // continuar como deslogado, em vez de pendurar a aplicação.
          clearLocalAuthState();
          initialSession = null;
        }

        if (!mounted) return;

        applyBasicSession(initialSession);
        if (initialSession?.user) {
          // `hydrateAccessData` corre síncrono até ao primeiro await, logo o
          // `setHydrating(true)` lá dentro acontece ANTES do setLoading(false)
          // abaixo. É isso que fecha a janela entre "sessão pronta" e
          // "perfil carregado".
          void hydrateAccessData(initialSession.user.id);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [applyBasicSession, hydrateAccessData, resetReactAuthState]);

  const refreshProfile = useCallback(async () => {
    if (user?.id) await hydrateAccessData(user.id);
  }, [user?.id, hydrateAccessData]);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { error } = await withTimeout(
        supabase.auth.signInWithPassword({ email, password }),
        SIGN_IN_TIMEOUT_MS,
        "SIGN_IN",
      );
      return { error };
    } catch (err: any) {
      if (err?.message === "SIGN_IN_TIMEOUT") {
        return { error: { message: "FALHA_TECNICA" } };
      }
      return { error: err };
    }
  }, []);

  /**
   * Registo. O perfil em si é criado pelo trigger `handle_new_user()` com
   * `approved = false` — aqui só completamos o nome. Um utilizador novo fica
   * pendente até um administrador o aprovar e lhe atribuir um projeto.
   */
  const signUp = useCallback(
    async (data: { email: string; password: string; fullName: string }) => {
      const { data: authData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: { data: { full_name: data.fullName } },
      });
      if (error) return { error };

      if (authData.user) {
        const { error: profileError } = await supabase
          .from("profiles")
          .update({ full_name: data.fullName, email: data.email })
          .eq("user_id", authData.user.id);
        if (profileError) {
          // Não é fatal: o trigger já criou a linha e o utilizador fica pendente.
          console.error("Falha ao completar o perfil:", profileError);
        }
      }
      return { error: null };
    },
    [],
  );

  /**
   * Troca a senha do próprio utilizador. GoTrue aceita-a pela sessão ativa —
   * não passa pela RPC de admin (`admin_redefinir_senha`), que é para um admin
   * redefinir a de OUTROS. Por compliance, cada pessoa define a sua.
   */
  const updatePassword = useCallback(async (novaSenha: string) => {
    const { error } = await supabase.auth.updateUser({ password: novaSenha });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    try {
      await Promise.race([
        supabase.auth.signOut(),
        new Promise((resolve) => setTimeout(resolve, SIGN_OUT_TIMEOUT_MS)),
      ]);
    } catch {
      /* a rede pode falhar; a limpeza local acontece de qualquer forma */
    }
    clearLocalAuthState();
    resetReactAuthState();
  }, [resetReactAuthState]);

  const isApproved = profile?.approved ?? false;

  const hasPermission = useCallback(
    (module: string, action: PermissionAction): boolean => {
      if (isAdmin) return true;
      const perm = modulePermissions.find((p) => p.module === module);
      if (!perm) return false;
      switch (action) {
        case "read":
          return perm.can_read;
        case "create":
          return perm.can_create;
        case "update":
          return perm.can_update;
        case "delete":
          return perm.can_delete;
        default:
          return false;
      }
    },
    [isAdmin, modulePermissions],
  );

  const temCapacidade = useCallback(
    (capacidade: Capacidade) => isAdmin || capacidades[capacidade] === true,
    [isAdmin, capacidades],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        isAdmin,
        isApproved,
        modules,
        modulePermissions,
        loading,
        hydrating,
        signIn,
        signUp,
        signOut,
        updatePassword,
        refreshProfile,
        hasPermission,
        papel,
        temCapacidade,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth tem de ser usado dentro de AuthProvider");
  return ctx;
}
